import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Container,
  Box,
  VStack,
  Heading,
  Text,
  Flex,
  Button,
  ButtonGroup,
  Card,
  CardHeader,
  CardBody,
  CardFooter,
  Stack,
  Badge,
  Skeleton,
  useToast,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useColorModeValue,
  Divider,
  IconButton,
  Tooltip,
  HStack,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  SimpleGrid,
  Icon,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
} from '@chakra-ui/react';
import { 
  FileText, 
  Clock, 
  TrashSimple, 
  PencilSimple, 
  FloppyDisk, 
  CaretUp, 
  CaretDown, 
  DotsThreeVertical,
  WarningCircle,
  CheckCircle,
  ArrowLeft
} from '@phosphor-icons/react';
import { getDrafts, deleteDraft } from '../firebase/siteDrafts';
import { auth } from '../firebase/config';

// Format the date in a user-friendly way
const formatDate = (date) => {
  if (!date) return 'Unknown date';
  
  const now = new Date();
  const diffTime = Math.abs(now - date);
  const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
  
  if (diffDays === 0) {
    return 'Today';
  } else if (diffDays === 1) {
    return 'Yesterday';
  } else if (diffDays < 7) {
    return `${diffDays} days ago`;
  } else if (diffDays < 30) {
    return `${Math.floor(diffDays / 7)} weeks ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Calculate days remaining until expiration
const getDaysRemaining = (expiresAt) => {
  if (!expiresAt) return null;
  
  const now = new Date();
  const diffTime = expiresAt - now;
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Format draft status as a badge
const DraftStatusBadge = ({ expiresAt }) => {
  if (!expiresAt) return null;
  
  const daysRemaining = getDaysRemaining(expiresAt);
  
  if (daysRemaining <= 0) {
    return (
      <Badge colorScheme="red" variant="solid" px={2} py={1} borderRadius="full">
        Expired
      </Badge>
    );
  } else if (daysRemaining <= 7) {
    return (
      <Badge colorScheme="orange" variant="solid" px={2} py={1} borderRadius="full">
        Expires soon ({daysRemaining} days)
      </Badge>
    );
  } else if (daysRemaining <= 30) {
    return (
      <Badge colorScheme="yellow" variant="subtle" px={2} py={1} borderRadius="full">
        {daysRemaining} days left
      </Badge>
    );
  } else {
    return (
      <Badge variant="subtle" bg="ancient.subtle" color="ancient.stone" px={2} py={1} borderRadius="full">
        {daysRemaining} days left
      </Badge>
    );
  }
};

export default function Drafts() {
  const navigate = useNavigate();
  const toast = useToast();
  const [drafts, setDrafts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [sorting, setSorting] = useState('updatedAt');
  const [sortDirection, setSortDirection] = useState('desc');
  
  // For delete confirmation dialog
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [draftToDelete, setDraftToDelete] = useState(null);
  const cancelRef = React.useRef();
  
  // For publish confirmation dialog
  const [isPublishDialogOpen, setIsPublishDialogOpen] = useState(false);
  const [draftToPublish, setDraftToPublish] = useState(null);
  
  // Theme
  const bgGradient = useColorModeValue(
    'ancient.background',
    'linear(to-r, gray.900, gray.800)'
  );
  const cardBg = useColorModeValue('white', 'gray.700');
  const cardHeaderBg = useColorModeValue('gray.50', 'gray.800');

  // Fetch user drafts
  useEffect(() => {
    const fetchDrafts = async () => {
      try {
        setLoading(true);
        const result = await getDrafts();
        
        if (result.success) {
          setDrafts(result.data);
        } else {
          toast({
            title: 'Error loading drafts',
            description: result.error,
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        }
      } catch (error) {
        console.error('Error fetching drafts:', error);
        toast({
          title: 'Error loading drafts',
          description: 'Failed to load drafts',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      } finally {
        setLoading(false);
      }
    };
    
    if (auth.currentUser) {
      fetchDrafts();
    } else {
      navigate('/login');
    }
  }, [navigate, toast]);

  // Sort drafts
  const sortedDrafts = [...drafts].sort((a, b) => {
    let valA, valB;

    if (sorting === 'name') {
      valA = a.siteData?.name?.toLowerCase() || '';
      valB = b.siteData?.name?.toLowerCase() || '';
    } else if (sorting === 'updatedAt' || sorting === 'createdAt') {
      // Timestamps are directly on the draft object
      valA = a[sorting] || '';
      valB = b[sorting] || '';
    } else {
      // Fallback for any other direct properties, though 'name' is the main one needing siteData
      valA = a[sorting] || '';
      valB = b[sorting] || '';
    }
    
    if (valA === valB) return 0;
    const compareResult = valA > valB ? 1 : -1;
    return sortDirection === 'asc' ? compareResult : -compareResult;
  });

  // Toggle sort direction
  const toggleSortDirection = () => {
    setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
  };

  // Change sort field
  const changeSortField = (field) => {
    if (field === sorting) {
      toggleSortDirection();
    } else {
      setSorting(field);
      setSortDirection('desc'); // Default to newest first
    }
  };

  // Handle delete draft
  const handleDeleteDraft = async () => {
    if (!draftToDelete) return;
    
    try {
      const result = await deleteDraft(draftToDelete.id);
      
      if (result.success) {
        // Remove from list
        setDrafts(prev => prev.filter(d => d.id !== draftToDelete.id));
        
        toast({
          title: 'Draft deleted',
          description: 'Draft has been permanently deleted',
          status: 'success',
          duration: 3000,
          isClosable: true
        });
      } else {
        toast({
          title: 'Error deleting draft',
          description: result.error,
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    } catch (error) {
      console.error('Error deleting draft:', error);
      toast({
        title: 'Error deleting draft',
        description: 'Failed to delete draft',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsDeleteDialogOpen(false);
      setDraftToDelete(null);
    }
  };

  // Handle publish draft
  const handlePublishDraft = async () => {
    if (!draftToPublish) return;
    
    toast({
      title: 'Publishing not available',
      description: 'This feature is temporarily unavailable. Please edit the draft and publish from the create site page.',
      status: 'info',
      duration: 5000,
      isClosable: true
    });
    
    setIsPublishDialogOpen(false);
    setDraftToPublish(null);
    
    // Navigate to edit the draft instead
    navigate(`/create-site?draft=${draftToPublish.id}`);
  };

  return (
    <Box 
      bgGradient={bgGradient}
      minH="calc(100vh - 80px)"
      py={8}
      position="relative"
      overflow="hidden"
    >
      {/* Decorative elements */}
      <Box
        position="absolute"
        top={-20}
        right={-20}
        width="240px"
        height="240px"
        borderRadius="full"
        bg="ancient.stone"
        opacity={0.02}
        zIndex={0}
      />
      <Box
        position="absolute"
        bottom={-40}
        left={-40}
        width="320px"
        height="320px"
        borderRadius="full"
        bg="ancient.terracotta"
        opacity={0.02}
        zIndex={0}
      />

      <Container maxW="container.xl" position="relative" zIndex={1}>
        <VStack align="stretch" spacing={8}>
          <Flex 
            justify="space-between" 
            align="center" 
            wrap="wrap"
            gap={4}
          >
            <Box>
              <Button
                leftIcon={<ArrowLeft weight="bold" />}
                variant="ghost" 
                mb={2}
                onClick={() => navigate('/dashboard')}
              >
                Back to Dashboard
              </Button>
              <Heading size="xl" mb={1}>Draft Sites</Heading>
              <Text color="gray.600" _dark={{ color: 'gray.400' }}>
                Manage your saved work-in-progress
              </Text>
            </Box>
            
            <HStack>
              <Text fontSize="sm" color="gray.500">Sort by:</Text>
              <Menu>
                <MenuButton
                  as={Button}
                  rightIcon={sortDirection === 'asc' ? <CaretUp /> : <CaretDown />}
                  variant="outline"
                  size="sm"
                >
                  {sorting === 'updatedAt' ? 'Last Updated' :
                   sorting === 'createdAt' ? 'Date Created' :
                   sorting === 'name' ? 'Name (A-Z)' : 'Last Updated'}
                </MenuButton>
                <MenuList>
                  <MenuItem onClick={() => changeSortField('updatedAt')}>
                    Last Updated {sorting === 'updatedAt' && (
                      <Icon as={sortDirection === 'asc' ? CaretUp : CaretDown} ml={2} />
                    )}
                  </MenuItem>
                  <MenuItem onClick={() => changeSortField('createdAt')}>
                    Date Created {sorting === 'createdAt' && (
                      <Icon as={sortDirection === 'asc' ? CaretUp : CaretDown} ml={2} />
                    )}
                  </MenuItem>
                  <MenuItem onClick={() => changeSortField('name')}>
                    Name {sorting === 'name' && (
                      <Icon as={sortDirection === 'asc' ? CaretUp : CaretDown} ml={2} />
                    )}
                  </MenuItem>
                </MenuList>
              </Menu>
              
              <Button 
                variant="relief"
                onClick={() => navigate('/create-site')}
              >
                New Site
              </Button>
            </HStack>
          </Flex>

          {/* Draft List */}
          {loading ? (
            <Stack spacing={4}>
              {[1, 2, 3].map(i => (
                <Card key={i} boxShadow="md">
                  <CardHeader>
                    <Skeleton height="20px" width="200px" mb={2} />
                    <Skeleton height="16px" width="140px" />
                  </CardHeader>
                  <CardBody>
                    <Skeleton height="60px" />
                  </CardBody>
                  <CardFooter>
                    <Skeleton height="36px" width="100px" mx={2} />
                    <Skeleton height="36px" width="100px" mx={2} />
                  </CardFooter>
                </Card>
              ))}
            </Stack>
          ) : sortedDrafts.length === 0 ? (
            <Card p={6}>
              <VStack spacing={4}>
                <Icon as={FileText} boxSize={12} color="ancient.stone" />
                <Heading size="md">No drafts found</Heading>
                <Text textAlign="center">
                  You haven't saved any draft sites yet. Start creating a new site and save your progress.
                </Text>
                <Button 
                  variant="relief"
                  leftIcon={<PencilSimple weight="bold" />}
                  onClick={() => navigate('/create-site')}
                >
                  Create New Site
                </Button>
              </VStack>
            </Card>
          ) : (
            <>
              <Alert status="info" variant="left-accent" mb={4}>
                <AlertIcon />
                <Box>
                  <AlertTitle>Draft Limit: 5 maximum</AlertTitle>
                  <AlertDescription>
                    Drafts are automatically deleted after 100 days of inactivity.
                  </AlertDescription>
                </Box>
              </Alert>
            
              <SimpleGrid columns={{ base: 1, md: 2, lg: 3 }} spacing={6}>
                {sortedDrafts.map(draft => {
                  const daysRemaining = draft.expiresAt ? getDaysRemaining(draft.expiresAt) : null;
                  const isExpiringSoon = daysRemaining !== null && daysRemaining <= 7 && daysRemaining > 0;
                  const isExpired = daysRemaining !== null && daysRemaining <= 0;
                  
                  return (
                    <Card 
                      key={draft.id}
                      bg={cardBg}
                      boxShadow="md"
                      borderWidth="1px"
                      borderColor={isExpiringSoon ? 'orange.200' : isExpired ? 'red.200' : 'gray.200'}
                      _dark={{
                        borderColor: isExpiringSoon ? 'orange.700' : isExpired ? 'red.700' : 'gray.600'
                      }}
                      transition="all 0.2s"
                      _hover={{
                        transform: 'translateY(-2px)',
                        boxShadow: 'lg'
                      }}
                    >
                      <CardHeader 
                        bg={cardHeaderBg} 
                        borderBottom="1px" 
                        borderColor="gray.200"
                        _dark={{ borderColor: 'gray.600' }}
                        pb={3}
                      >
                        <Flex justify="space-between" align="flex-start">
                          <Box>
                            <Heading size="md" noOfLines={1}>
                              {draft.siteData?.name || 'Untitled Site'}
                            </Heading>
                            <HStack mt={1.5} spacing={2}>
                              <Icon as={Clock} weight="bold" color="gray.500" />
                              <Text fontSize="xs" color="gray.500">
                                Last edited {formatDate(new Date(draft.updatedAt))}
                              </Text>
                            </HStack>
                          </Box>
                          <DraftStatusBadge expiresAt={draft.siteData?.expiresAt} />
                        </Flex>
                      </CardHeader>
                      
                      <CardBody>
                        <Text noOfLines={3} fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
                          {draft.siteData?.description || 'No description provided.'}
                        </Text>
                        
                        {/* Location Info */}
                        {draft.siteData?.location?.address && (
                          <HStack mt={3} fontSize="sm" color="gray.600" _dark={{ color: 'gray.400' }}>
                            <Text fontWeight="medium">Location:</Text>
                            <Text noOfLines={1}>
                              {draft.siteData.location.address}
                              {draft.siteData.location.country ? `, ${draft.siteData.location.country}` : ''}
                            </Text>
                          </HStack>
                        )}
                      </CardBody>
                      
                      <Divider />
                      
                      <CardFooter>
                        <ButtonGroup spacing={2}>
                          <Button
                            leftIcon={<PencilSimple weight="bold" />}
                            variant="tablet"
                            onClick={() => navigate(`/create-site?draftId=${draft.id}`)}
                          >
                            Edit
                          </Button>
                          
                          <Menu>
                            <MenuButton
                              as={IconButton}
                              aria-label="Options"
                              icon={<DotsThreeVertical weight="bold" />}
                              variant="ghost"
                            />
                            <MenuList>
                              <MenuItem 
                                icon={<FloppyDisk weight="bold" />}
                                onClick={() => {
                                  setDraftToPublish(draft);
                                  setIsPublishDialogOpen(true);
                                }}
                              >
                                Publish Site
                              </MenuItem>
                              <MenuItem 
                                icon={<TrashSimple weight="bold" />}
                                color="red.500"
                                _dark={{ color: 'red.300' }}
                                onClick={() => {
                                  setDraftToDelete(draft);
                                  setIsDeleteDialogOpen(true);
                                }}
                              >
                                Delete
                              </MenuItem>
                            </MenuList>
                          </Menu>
                        </ButtonGroup>
                      </CardFooter>
                    </Card>
                  );
                })}
              </SimpleGrid>
            </>
          )}
        </VStack>
      </Container>

      {/* Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isDeleteDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsDeleteDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Delete Draft
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Flex>
                  <Icon as={WarningCircle} color="red.500" boxSize={5} mr={2} />
                  <Text>
                    Are you sure you want to delete this draft? This action cannot be undone.
                  </Text>
                </Flex>
                
                {draftToDelete && (
                  <Box p={3} bg="gray.50" borderRadius="md" _dark={{ bg: 'gray.700' }}>
                    <Text fontWeight="bold">
                      {draftToDelete.siteData?.name || 'Untitled Site'}
                    </Text>
                    <Text fontSize="sm" noOfLines={2} color="gray.600" _dark={{ color: 'gray.400' }}>
                      {draftToDelete.siteData?.description || 'No description'}
                    </Text>
                  </Box>
                )}
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsDeleteDialogOpen(false)}>
                Cancel
              </Button>
              <Button colorScheme="red" onClick={handleDeleteDraft} ml={3}>
                Delete
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Publish Confirmation Dialog */}
      <AlertDialog
        isOpen={isPublishDialogOpen}
        leastDestructiveRef={cancelRef}
        onClose={() => setIsPublishDialogOpen(false)}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Publish Draft
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="stretch" spacing={4}>
                <Flex>
                  <Icon as={CheckCircle} color="green.500" boxSize={5} mr={2} />
                  <Text>
                    Are you ready to publish this draft as a site? This will make it visible to other users.
                  </Text>
                </Flex>
                
                {draftToPublish && (
                  <Box p={3} bg="green.50" borderRadius="md" _dark={{ bg: 'green.900' }}>
                    <Text fontWeight="bold" color="green.800" _dark={{ color: 'green.200' }}>
                      {draftToPublish.siteData?.name || 'Untitled Site'}
                    </Text>
                    <Text fontSize="sm" noOfLines={2} color="green.700" _dark={{ color: 'green.300' }}>
                      {draftToPublish.siteData?.description || 'No description'}
                    </Text>
                  </Box>
                )}
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={() => setIsPublishDialogOpen(false)}>
                Cancel
              </Button>
              <Button variant="relief" onClick={handlePublishDraft} ml={3}>
                Publish Site
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </Box>
  );
}
